'use client';

import { useState, useEffect } from 'react';
import PNRStatusCard from './PNRStatusCard';
import PNRSearchBar from './PNRSearchBar';
import StatusFilter from './StatusFilter';
import RealtimeActivityFeed from './RealtimeActivityFeed';
import ExportActions from './ExportActions';

interface PNRStatus {
  pnr: string;
  status: 'confirmed' | 'pending' | 'cancelled' | 'waitlisted';
  airline: string;
  airlineLogo: string;
  route: string;
  departure: string;
  arrival: string;
  passenger: string;
  bookingRef: string;
  seatNumber: string;
  lastUpdated: string;
}

interface Activity {
  id: string;
  type: 'confirmation' | 'update' | 'alert' | 'cancellation';
  message: string;
  pnr: string;
  timestamp: string;
}

interface DashboardInteractiveProps {
  initialPNRs: PNRStatus[];
  initialActivities: Activity[];
}

const DashboardInteractive = ({ initialPNRs, initialActivities }: DashboardInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [pnrs, setPnrs] = useState(initialPNRs);
  const [searchResults, setSearchResults] = useState<PNRStatus[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleSearch = (searchTerm: string) => {
    setIsSearching(true);
    const results = initialPNRs.filter(
      (pnr) =>
        pnr.pnr.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pnr.bookingRef.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSearchResults(results);
    setTimeout(() => setIsSearching(false), 500);
  };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setIsSearching(false);
    setSearchResults([]);
  };

  const getFilteredPNRs = () => {
    if (isSearching || searchResults.length > 0) {
      return searchResults;
    }
    if (activeFilter === 'all') {
      return pnrs;
    }
    return pnrs.filter((pnr) => pnr.status === activeFilter);
  };

  const filteredPNRs = getFilteredPNRs();

  if (!isHydrated) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-muted rounded-lg"></div>
          <div className="h-16 bg-muted rounded-lg"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PNRSearchBar onSearch={handleSearch} />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <StatusFilter activeFilter={activeFilter} onFilterChange={handleFilterChange} />
        <ExportActions />
      </div>

      {filteredPNRs.length === 0 ? (
        <div className="bg-card rounded-lg shadow-brand p-12 border border-border text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
            No bookings found
          </h3>
          <p className="text-sm text-muted-foreground">
            {isSearching || searchResults.length === 0 && activeFilter === 'all' ?'Try adjusting your search criteria'
              : `No ${activeFilter} bookings available`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPNRs.map((pnr) => (
            <PNRStatusCard key={pnr.pnr} pnr={pnr} />
          ))}
        </div>
      )}

      <RealtimeActivityFeed activities={initialActivities} />
    </div>
  );
};

export default DashboardInteractive;